import { flyingTulipAssetsFor, flyingTulipConfigFor } from '@1delta/data-sdk'
import { parseRawAmount } from '../../../utils/parsing'
import { isFailedCall } from '../../../utils/multicall'
import { createMarketUid } from '../../../utils'
import { getDisplayPrice, getOraclePrice } from '../utils/oraclePrice'
import { createMultiAccountTypeUserState } from '../utils'
import { LenderCrossPoolMeta, UserData } from '../utils/types'
import { FLYING_TULIP_USER_CALLS } from './userCallBuild'

/**
 * Account-level Flying Tulip extras, attached to EVERY row of the account
 * (cross-margin: the numbers belong to the whole account, not a market).
 */
export interface FlyingTulipAccountInfo {
  /** Protocol equity, USD-WAD string. FOLDS `enginePnL` in — authoritative. */
  equityUSDWad: string
  /** Maintenance requirement, USD-WAD string — charged on BOTH legs. */
  maintUSDWad: string
  /**
   * The protocol's OWN health factor, 1e4 scale. Liquidation triggers BELOW
   * 12500 (1.25) — never apply `HF < 1 ⇒ liquidatable` here — and new
   * borrows/withdrawals must leave it ≥ 15000 (1.50). Saturates at 65535
   * ONLY for an empty account (maint == 0): a debt-free account holding
   * collateral reads a FINITE value (1e8/mmBps in the sole-asset case),
   * because maintenance is charged on the collateral leg too.
   */
  hfBps: number
}

const big = (v: any): bigint => {
  try {
    if (typeof v === 'bigint') return v
    if (typeof v === 'number' || typeof v === 'string') return BigInt(v)
  } catch {}
  return 0n
}

/**
 * Returns a [converter, expectedCallCount] tuple for Flying Tulip user data.
 *
 * ONE `userPortfolio(account)` call parsed into a single sub-account `'0'`
 * with one row per asset the account actually touches — the Exactly-shaped
 * cross-margin collapse (`FLYING_TULIP` is both dispatch and market key;
 * marketUids are `FLYING_TULIP:<chainId>:<asset>`).
 *
 * Two protocol facts the parse must respect:
 *  - Collateral is `avail + hold` — `hold` is engine-escrowed but still the
 *    user's balance; reporting only `avail` understates the position.
 *  - Amounts are in each asset's OWN decimals (USDC/ftUSD 6, WBTC 8, rest
 *    18) — resolve from the roster, never assume WAD.
 *
 * WHY THIS MODULE IS LOAD-BEARING: the converter dispatch in
 * `fetch-balances/parse.ts` deliberately falls through to the Compound V2
 * converter for any unmatched lender (the V2 fork zoo is the long tail).
 * The day `getLendersForChain` learned to enumerate `FLYING_TULIP`, every
 * user-data fetch on chains 1/146 fed a cross-margin lender into that
 * fallthrough and the whole read broke — a dispatch branch is not optional
 * for a new lender family, it is the difference between "no data" and
 * "wrong converter for everyone".
 */
export const getFlyingTulipUserDataConverter = (
  lender: string,
  chainId: string,
  account: string,
  meta?: LenderCrossPoolMeta,
): [(data: any[]) => UserData | undefined, number] => {
  const cfg = flyingTulipConfigFor(lender, chainId)
  const roster = flyingTulipAssetsFor(lender, chainId)
  const expected = cfg?.lendingLens ? FLYING_TULIP_USER_CALLS : 0

  const decimalsOf = (asset: string): number =>
    roster?.assets.find((a) => a.address.toLowerCase() === asset.toLowerCase())
      ?.decimals ?? 18

  return [
    (data: any[]) => {
      if (!cfg || expected === 0 || !meta) return undefined
      if (!data || data.length < expected) return undefined

      const raw = data[0]
      // A failed read is NOT an empty position — publish nothing rather than
      // reporting the user as position-free (the Cooler rule).
      if (isFailedCall(raw)) return undefined

      // 8-output function: decoders hand back a positional array; tolerate a
      // named object too.
      const p = Array.isArray(raw)
        ? {
            collAssets: raw[0],
            collAvail: raw[1],
            collHold: raw[2],
            debtAssets: raw[3],
            debtUnitsNow: raw[4],
            equityUSDWad: raw[5],
            maintUSDWad: raw[6],
            hfBps: raw[7],
          }
        : raw
      if (!p || !Array.isArray(p.collAssets) || !Array.isArray(p.debtAssets))
        return undefined

      const info: FlyingTulipAccountInfo = {
        equityUSDWad: big(p.equityUSDWad).toString(),
        maintUSDWad: big(p.maintUSDWad).toString(),
        hfBps: Number(p.hfBps ?? 0),
      }

      const rows: Record<string, any> = {}
      let any = false
      let deposits24h = 0
      let debt24h = 0

      for (let i = 0; i < p.collAssets.length; i++) {
        const asset = String(p.collAssets[i]).toLowerCase()
        const amount = big(p.collAvail?.[i]) + big(p.collHold?.[i])
        if (amount === 0n) continue
        const uid = createMarketUid(chainId, lender, asset)
        const m = meta[uid]
        const dec = decimalsOf(asset)
        const amtStr = parseRawAmount(amount.toString(), dec)
        const amtNum = Number(amtStr)
        const display = m ? getDisplayPrice(m) : 0
        const oracle = m ? getOraclePrice(m) : 0
        deposits24h += amtNum * (m?.price?.priceUsd24h ?? display)
        any = true
        rows[uid] = {
          marketUid: uid,
          underlying: asset,
          deposits: amtStr,
          debt: '0',
          debtStable: '0',
          depositsUSD: amtNum * display,
          debtUSD: 0,
          debtStableUSD: 0,
          depositsUSDOracle: amtNum * oracle,
          debtUSDOracle: 0,
          debtStableUSDOracle: 0,
          stableBorrowRate: '0',
          collateralEnabled: true,
          claimableRewards: 0,
          flyingTulipInfo: info,
        }
      }

      for (let i = 0; i < p.debtAssets.length; i++) {
        const asset = String(p.debtAssets[i]).toLowerCase()
        const amount = big(p.debtUnitsNow?.[i])
        if (amount === 0n) continue
        const uid = createMarketUid(chainId, lender, asset)
        const m = meta[uid]
        const dec = decimalsOf(asset)
        const amtStr = parseRawAmount(amount.toString(), dec)
        const amtNum = Number(amtStr)
        const display = m ? getDisplayPrice(m) : 0
        const oracle = m ? getOraclePrice(m) : 0
        debt24h += amtNum * (m?.price?.priceUsd24h ?? display)
        any = true
        const existing = rows[uid]
        if (existing) {
          // Same asset on both legs (legal under cross-margin): one row, both
          // sides — two rows under one uid would double-render the market.
          existing.debt = amtStr
          existing.debtUSD = amtNum * display
          existing.debtUSDOracle = amtNum * oracle
        } else {
          rows[uid] = {
            marketUid: uid,
            underlying: asset,
            deposits: '0',
            debt: amtStr,
            debtStable: '0',
            depositsUSD: 0,
            debtUSD: amtNum * display,
            debtStableUSD: 0,
            depositsUSDOracle: 0,
            debtUSDOracle: amtNum * oracle,
            debtStableUSDOracle: 0,
            stableBorrowRate: '0',
            collateralEnabled: false,
            claimableRewards: 0,
            flyingTulipInfo: info,
          }
        }
      }

      if (!any) return undefined

      const lendingPositions: Record<string, any> = { '0': rows }
      const modes: Record<string, number> = { '0': 0 }
      const hist = {
        '0': { totalDeposits24h: deposits24h, totalDebt24h: debt24h },
      }

      const userData = createMultiAccountTypeUserState(
        { chainId, account, lendingPositions, modes } as any,
        meta,
        hist,
      )
      return userData && userData.data.length > 0 ? userData : undefined
    },
    expected,
  ]
}
